import axiosV2Instance from './axiosV2Instance';

export interface IncomingPaymentV2 {
  id: number;
  siteId: number;
  typeOfWorkId: number;
  amount: number;
  paymentDate: string;
  description: string;
  paymentMethod?: string;
  invoiceNumber?: string;
  clientName?: string;
  isConfirmed: boolean;
  createdDate: string;
  createdBy?: number;
  isDeleted: boolean;
}

export interface CreateIncomingPaymentV2Request {
  typeOfWorkId: number;
  amount: number;
  paymentDate: string;
  description: string;
  paymentMethod?: string;
  invoiceNumber?: string;
  clientName?: string;
  isConfirmed: boolean;
}

export interface UpdateIncomingPaymentV2Request {
  typeOfWorkId: number;
  amount: number;
  paymentDate: string;
  description: string;
  paymentMethod?: string;
  invoiceNumber?: string;
  clientName?: string;
  isConfirmed: boolean;
}

export interface IncomingPaymentsStatisticsV2 {
  totalAmount: number;
  totalCount: number;
  monthAmount: number;
  monthCount: number;
  averageAmount: number;
}

class IncomingPaymentsV2Api {
  /**
   * Получить все платежи клиентов для объекта
   */
  async getIncomingPaymentsForSite(siteId: number): Promise<IncomingPaymentV2[]> {
    try {
      const response = await axiosV2Instance.get(
        `/sites/${siteId}/incoming-payments`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching incoming payments for site:', error);
      throw error;
    }
  }

  /**
   * Получить конкретный платеж клиента
   */
  async getIncomingPayment(siteId: number, paymentId: number): Promise<IncomingPaymentV2> {
    try {
      const response = await axiosV2Instance.get(
        `/sites/${siteId}/incoming-payments/${paymentId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching incoming payment:', error);
      throw error;
    }
  }

  /**
   * Получить статистику по платежам клиентов для объекта
   */
  async getStatisticsForSite(
    siteId: number,
    startDate?: string,
    endDate?: string
  ): Promise<IncomingPaymentsStatisticsV2> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const url = `/sites/${siteId}/incoming-payments/statistics${
        params.toString() ? '?' + params.toString() : ''
      }`;

      const response = await axiosV2Instance.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }

  /**
   * Проверить права пользователя на редактирование финансов объекта
   */
  async checkEditPermission(siteId: number): Promise<boolean> {
    try {
      const response = await axiosV2Instance.get(
        `/sites/${siteId}/incoming-payments/permissions/edit`
      );
      return response.data.canEdit;
    } catch (error) {
      console.error('Error checking edit permission:', error);
      return false;
    }
  }

  /**
   * Создать новый платеж клиента
   */
  async createIncomingPayment(
    siteId: number,
    payment: CreateIncomingPaymentV2Request
  ): Promise<number> {
    try {
      const response = await axiosV2Instance.post(
        `/sites/${siteId}/incoming-payments`,
        payment
      );
      return response.data.id;
    } catch (error) {
      console.error('Error creating incoming payment:', error);
      throw error;
    }
  }

  /**
   * Обновить платеж клиента
   */
  async updateIncomingPayment(
    siteId: number,
    paymentId: number,
    payment: UpdateIncomingPaymentV2Request
  ): Promise<void> {
    try {
      await axiosV2Instance.put(
        `/sites/${siteId}/incoming-payments/${paymentId}`,
        payment
      );
    } catch (error) {
      console.error('Error updating incoming payment:', error);
      throw error;
    }
  }

  /**
   * Удалить платеж клиента
   */
  async deleteIncomingPayment(siteId: number, paymentId: number): Promise<void> {
    try {
      await axiosV2Instance.delete(
        `/sites/${siteId}/incoming-payments/${paymentId}`
      );
    } catch (error) {
      console.error('Error deleting incoming payment:', error);
      throw error;
    }
  }
}

export const incomingPaymentsV2Api = new IncomingPaymentsV2Api();

